import { useMemo, useState } from 'react';
import Dropdown, { useDropdownMenu } from '../Dropdown.jsx';
import Tooltip from '../Tooltip.jsx';
import TopbarApplicationIcon from './TopbarApplicationIcon.jsx';
import TopbarChip from './TopbarChip.jsx';
import {
  dropdownMenuHeaderClass,
  dropdownPanelClass,
  dropdownPanelTopDividerClass,
  dropdownWorkspaceRowClass,
  topbarApplicationsMenuIconShellClass,
} from './topbarClasses.js';
import { Check } from 'lucide-react';

export const TopbarApplicationsPanel = ({
  applications = [],
  selectedApplicationId,
  onSelectApplication,
  showApplicationDescriptions = false,
  title = 'Workspaces',
}) => {
  const menu = useDropdownMenu();

  return (
  <div className={dropdownPanelClass}>
    <div className={dropdownPanelTopDividerClass} aria-hidden />
    <div className={dropdownMenuHeaderClass}>{title}</div>
    {applications.map((application) => {
      const Icon = application.icon;
      const isActive = selectedApplicationId === application.id;
      const shouldShowDescription = application.showDescription ?? showApplicationDescriptions;

      return (
        <button
          key={application.id}
          type="button"
          onClick={() => { onSelectApplication?.(application); menu?.close?.(); }}
          aria-current={isActive ? 'page' : undefined}
          className={[
            dropdownWorkspaceRowClass,
            shouldShowDescription ? 'items-start' : '',
            isActive ? 'bg-[#F6F9FF] dark:bg-surface-hover-subtle' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div
            className={[
              shouldShowDescription ? 'mt-0.5' : '',
              'flex h-8 w-8 shrink-0 items-center justify-center',
              isActive ? 'text-[#2173FF]' : 'text-content-muted',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {Icon ? <Icon size={16} aria-hidden /> : null}
          </div>
          {shouldShowDescription ? (
            <div className="flex min-w-0 flex-1 flex-col items-start">
              <span className="w-full truncate text-left text-[12px] font-medium leading-none text-content-secondary">
                {application.title}
              </span>
              {application.description ? (
                <span className="truncate text-[11px] leading-none text-content-muted">
                  {application.description}
                </span>
              ) : null}
            </div>
          ) : (
            <span className="min-w-0 flex-1 truncate text-left text-[12px] font-medium leading-none text-content-secondary">
              {application.title}
            </span>
          )}
          {isActive ? (
            <Check size={16} className="shrink-0 text-[#2173FF]" aria-hidden />
          ) : null}
        </button>
      );
    })}
  </div>
  );
};

const TopbarApplicationsMenu = ({
  studio,
  applications = [],
  selectedApplicationId,
  onSelectApplication,
  showApplicationDescriptions = false,
  labels = {},
  studioTooltip,
  className = '',
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const selectedApplication = useMemo(
    () => applications.find((application) => application.id === selectedApplicationId) ?? null,
    [applications, selectedApplicationId],
  );
  const selectedStudioLabel =
    selectedApplication?.title || studio?.label || labels.workSurface || 'Work Surface';
  const SelectedStudioIcon = selectedApplication?.icon;
  const leftMenuTitle =
    labels.leftMenuTitle || labels.workspaces || labels.applications || 'Workspaces';

  const hasStudioTooltip =
    typeof studioTooltip === 'string'
      ? Boolean(studioTooltip.trim())
      : Boolean(studioTooltip?.title || studioTooltip?.description);

  const studioTooltipContent = useMemo(
    () =>
      typeof studioTooltip === 'string' ? (
        studioTooltip
      ) : (
        <div className="flex flex-col">
          {studioTooltip?.title ? (
            <span className="text-sm font-medium text-white">{studioTooltip.title}</span>
          ) : null}
          {studioTooltip?.description ? (
            <span className="text-sm font-normal text-white/90">{studioTooltip.description}</span>
          ) : null}
        </div>
      ),
    [studioTooltip],
  );

  const triggerContent = useMemo(
    () => (
      <div
        className={[className, 'inline-flex w-fit max-w-full min-w-0'].filter(Boolean).join(' ')}
        role="group"
        aria-label={selectedStudioLabel}
      >
        <TopbarChip
          label={selectedStudioLabel}
          hideLabelOnMobile
          logo={
            <TopbarApplicationIcon
              icon={SelectedStudioIcon}
              logo={studio?.logo || selectedApplication?.logo}
              shellClassName={topbarApplicationsMenuIconShellClass}
            />
          }
          avatarSrc={studio?.avatarSrc}
          avatarInitials={studio?.avatarInitials || 'ST'}
          labelMaxWidthClass="max-w-[10ch] md:max-w-[14ch]"
          withLabelTooltip={!hasStudioTooltip}
          tooltipContent={selectedStudioLabel}
          showChevron
          className="w-fit max-md:max-w-full"
        />
      </div>
    ),
    [className, selectedStudioLabel, studio, selectedApplication, SelectedStudioIcon, hasStudioTooltip],
  );

  return (
    <Dropdown
      align="left"
      usePortal
      menuMaxHeight="none"
      menuClassName="w-80 border-none bg-transparent p-0 shadow-none"
      onOpenChange={setMenuOpen}
      trigger={
        hasStudioTooltip && !menuOpen ? (
          <Tooltip
            content={studioTooltipContent}
            arrow={false}
            position="bottom"
            align="start"
            triggerClassName="inline-flex w-fit max-w-full"
          >
            {triggerContent}
          </Tooltip>
        ) : (
          triggerContent
        )
      }
    >
      <TopbarApplicationsPanel
        applications={applications}
        selectedApplicationId={selectedApplicationId}
        onSelectApplication={onSelectApplication}
        showApplicationDescriptions={showApplicationDescriptions}
        title={leftMenuTitle}
      />
    </Dropdown>
  );
};

export default TopbarApplicationsMenu;
